import type { AnimeNews, AnimeWork } from "@/types/anime";
import { animeNews } from "@/mocks/news";
import { animeWorks } from "@/mocks/works";

export const newsList = animeNews as unknown as AnimeNews[];
export const workList = animeWorks as unknown as AnimeWork[];

export function getNewsById(id: string): AnimeNews | undefined {
  return newsList.find((item) => item.id === id);
}

export function getWorkById(id: string): AnimeWork | undefined {
  return workList.find((item) => item.id === id);
}

export function getNewsByIds(ids: string[]): AnimeNews[] {
  return ids
    .map((id) => getNewsById(id))
    .filter((item): item is AnimeNews => Boolean(item));
}

export function getWorkByIds(ids: string[]): AnimeWork[] {
  return ids
    .map((id) => getWorkById(id))
    .filter((item): item is AnimeWork => Boolean(item));
}

export function sortNews(list: AnimeNews[], sort: "latest" | "hot"): AnimeNews[] {
  const copy = [...list];
  if (sort === "hot") {
    return copy.sort((a, b) => b.views - a.views);
  }
  return copy.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
}

export function filterNewsByCategory(list: AnimeNews[], category: string): AnimeNews[] {
  if (!category || category === "全部") return list;
  return list.filter((item) => item.category === category);
}

export interface WorkFilter {
  type?: string;
  genre?: string;
  region?: string;
  year?: string;
}

export function filterWorks(list: AnimeWork[], filter: WorkFilter): AnimeWork[] {
  return list.filter((item) => {
    if (filter.type && filter.type !== "全部" && item.type !== filter.type) return false;
    if (filter.genre && filter.genre !== "全部" && !item.genres.includes(filter.genre)) return false;
    if (filter.region && filter.region !== "全部" && item.region !== filter.region) return false;
    if (filter.year && filter.year !== "全部" && String(item.year) !== filter.year) return false;
    return true;
  });
}

export function searchNews(keyword: string): AnimeNews[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return newsList.filter((item) => {
    const haystack = [item.title, item.summary, item.category, ...item.tags].join(" ").toLowerCase();
    return haystack.includes(q);
  });
}

export function searchWorks(keyword: string): AnimeWork[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return workList.filter((item) => {
    const haystack = [item.title, item.synopsis, item.type, item.region, item.studio, ...item.genres]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelatedNews(news: AnimeNews, count = 4): AnimeNews[] {
  const sameCategory = newsList.filter((item) => item.id !== news.id && item.category === news.category);
  const sameTag = newsList.filter(
    (item) => item.id !== news.id && item.tags.some((tag) => news.tags.includes(tag)),
  );
  const merged = [...sameCategory, ...sameTag].filter(
    (item, index, arr) => arr.findIndex((n) => n.id === item.id) === index,
  );
  const rest = newsList.filter((item) => item.id !== news.id && !merged.some((m) => m.id === item.id));
  return [...merged, ...rest].slice(0, count);
}

export function getRelatedWorks(work: AnimeWork, count = 6): AnimeWork[] {
  const sameGenre = workList.filter(
    (item) => item.id !== work.id && item.genres.some((genre) => work.genres.includes(genre)),
  );
  const sameStudio = workList.filter((item) => item.id !== work.id && item.studio === work.studio);
  const merged = [...sameGenre, ...sameStudio].filter(
    (item, index, arr) => arr.findIndex((w) => w.id === item.id) === index,
  );
  const rest = workList.filter((item) => item.id !== work.id && !merged.some((m) => m.id === item.id));
  return [...merged, ...rest].slice(0, count);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

export const newsCategories = ["全部", "TV动画", "剧场版", "漫画新闻"];
export const workTypes = ["全部", "TV动画", "OVA", "剧场版"];
export const workGenres = ["全部", "奇幻", "动作", "剧情", "喜剧", "科幻", "爱情", "音乐", "悬疑", "历史", "日常", "热血"];
export const workRegions = ["全部", "日本", "国产"];
export const workYears = ["全部", "2023", "2022", "2021", "2020", "2019", "2016", "2013", "2011"];

export function getPageParam(search: string): number {
  const params = new URLSearchParams(search);
  const page = Number(params.get("page"));
  return Number.isFinite(page) && page > 0 ? page : 1;
}